import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ClubBook, ClubMeeting, VoteView } from "@pagemates/shared";
import { api, describeError } from "../lib/api";
import { Avatar } from "./ui";
import { Cover, authorsOf } from "./BookBits";
import { StartBook } from "./StartBook";
import { shelf } from "@pagemates/shared/copy";

const txt = shelf.nextBookVote;

/*
 * The vote on the next book, as it sits on the shelf's on-deck list
 * (PLAN.md §Picking the next book). The header says what's going on and gives
 * admins the open/close controls; `RankMark` and `Tally` decorate each row.
 */

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

export function useVote(clubId: string) {
  const qc = useQueryClient();
  const refresh = () => qc.invalidateQueries({ queryKey: ["books", clubId] });
  const ballot = useMutation({ mutationFn: (ranks: string[]) => api.setBallot(clubId, ranks), onSuccess: refresh });
  const open = useMutation({ mutationFn: (closesAt: string | null) => api.openVote(clubId, closesAt), onSuccess: refresh });
  const close = useMutation({ mutationFn: () => api.closeVote(clubId), onSuccess: refresh });
  const winner = useMutation({ mutationFn: (id: string) => api.pickWinner(clubId, id), onSuccess: refresh });
  const dismiss = useMutation({ mutationFn: () => api.dismissVote(clubId), onSuccess: refresh });
  return { ballot, open, close, winner, dismiss };
}

/** Tap to rank: gives the book your next number, or takes it back out. */
export function toggleRank(ranks: string[], id: string): string[] {
  return ranks.includes(id) ? ranks.filter((r) => r !== id) : [...ranks, id];
}

/** The header above the on-deck list while a vote is open or just closed, or the admin's "open a vote" prompt. */
export function VoteHeader({ clubId, vote, isAdmin, canStart, current, meeting, deckCount, onDeck }:
  { clubId: string; vote: VoteView | null; isAdmin: boolean; canStart: boolean; current: ClubBook | null; meeting: ClubMeeting | null; deckCount: number; onDeck: ClubBook[] }) {
  const v = useVote(clubId);
  const [opening, setOpening] = useState(false);
  const [closesAt, setClosesAt] = useState<string>(meeting?.at && new Date(meeting.at) > new Date() ? meeting.at : "");
  const err = v.open.error ?? v.close.error ?? v.winner.error ?? v.dismiss.error ?? v.ballot.error;

  if (!vote) {
    if (!isAdmin) return null;
    if (deckCount < 2) return null;
    return (
      <div className="vote-head idle">
        {!opening ? (
          <button type="button" className="linkish" onClick={() => setOpening(true)}>{txt.openAVoteOn}</button>
        ) : (
          <form className="stack-sm" onSubmit={(e) => { e.preventDefault(); v.open.mutate(closesAt || null); setOpening(false); }}>
            <b>{txt.openAVote}</b>
            <div className="d">{txt.theBooksOnDeck}</div>
            <div className="row" style={{ gap: 8, flexWrap: "wrap", alignItems: "center" }}>
              <label className="small muted" htmlFor="vote-closes">{txt.closes}</label>
              <select id="vote-closes" className="input" style={{ maxWidth: 240 }} value={closesAt} onChange={(e) => setClosesAt(e.target.value)}>
                <option value="">{txt.whenAnAdminCloses}</option>
                {meeting?.at && new Date(meeting.at) > new Date() && <option value={meeting.at}>{txt.atTheMeeting(when(meeting.at))}</option>}
                {[3, 7, 14].map((d) => { const t = new Date(Date.now() + d * 86_400_000); t.setHours(20, 0, 0, 0); return <option key={d} value={t.toISOString()}>{txt.inDays(d, when(t.toISOString()))}</option>; })}
              </select>
              <button type="submit" className="btn primary sm" disabled={v.open.isPending}>{txt.openTheVote}</button>
              <button type="button" className="btn ghost sm" onClick={() => setOpening(false)}>{txt.cancel}</button>
            </div>
          </form>
        )}
        {err && <p className="error small">{describeError(err)}</p>}
      </div>
    );
  }

  const title = (id: string | null) => onDeck.find((b) => b.id === id)?.book.title ?? txt.thatBook;
  if (vote.status === "open") {
    return (
      <div className="vote-head open">
        <div className="grow">
          <b>{txt.voteOnTheNext}</b>
          <div className="d">{txt.ofVoted(vote.closesAt ? txt.closes2(when(vote.closesAt)) : txt.openUntilAnAdmin, vote.voted, vote.members, vote.voted === 1 ? txt.has : txt.have)}</div>
          <div className="how">{vote.method === "approval"
            ? (vote.myRanks.length === 0 ? txt.tickEveryBookYou : txt.youVeVotedTap)
            : vote.myRanks.length === 0 ? txt.tapBooksInThe : vote.newSinceMyVote.length > 0 ? txt.aBookWasAdded : txt.youVeVotedTap2}</div>
        </div>
        {isAdmin && <button type="button" className="btn sm" disabled={v.close.isPending} onClick={() => v.close.mutate()}>{txt.closeTheVote}</button>}
        {err && <p className="error small" style={{ flexBasis: "100%" }}>{describeError(err)}</p>}
      </div>
    );
  }
  // Closed: the result is one card holding the chosen book (or the tied ones); the list below goes back to normal.
  const picks = vote.winnerId ? [vote.winnerId] : vote.tiedIds;
  const entry = (id: string) => vote.entries.find((e) => e.clubBookId === id);
  const runner = vote.winnerId && vote.runnerUpId ? entry(vote.runnerUpId) : null;
  return (
    <div className="vote-head closed result">
      <div className="row spread">
        <b>{vote.winnerId ? txt.theVoteIsIn : picks.length > 1 ? txt.itSATie : txt.theVoteClosed}</b>
        {isAdmin && <button type="button" className="linkish small muted" disabled={v.dismiss.isPending} onClick={() => v.dismiss.mutate()}>{txt.dismiss}</button>}
      </div>
      <div className="d">{txt.closedOfVoted(vote.closedAt ? when(vote.closedAt) : "", vote.voted, vote.members)}</div>
      {picks.length === 0 && <div className="how">{txt.nobodyRankedAnything}</div>}
      {picks.map((id) => { const b = onDeck.find((x) => x.id === id); const e = entry(id); if (!b || !e) return null; return (
        <div key={id} className="pick">
          <Cover book={b.book} size="sm" />
          <div className="pick-body">
            <div className="t">{b.book.title}</div>
            <div className="a">{authorsOf(b.book)}</div>
            <div className="pts">{e.points} {e.points === 1 ? txt.point : txt.points}</div>
            <div className="who">
              <span className="faces">{e.voters.map((vv) => <span key={vv.user.id} className="face"><Avatar name={vv.user.name} image={vv.user.image} /><u>{vv.rank}</u></span>)}</span>
              <span className="d">{txt.firstChoice(e.voters.filter((x) => x.rank === 1).length, e.voters.filter((x) => x.rank === 1).length === 1 ? "" : txt.s)}</span>
            </div>
          </div>
          {vote.winnerId && canStart && <div className="pick-act"><StartBook clubId={clubId} book={b} current={current} /></div>}
          {!vote.winnerId && isAdmin && <div className="pick-act"><button type="button" className="btn sm" disabled={v.winner.isPending} onClick={() => v.winner.mutate(id)}>{txt.pickThisOne}</button></div>}
        </div>
      ); })}
      {!vote.winnerId && picks.length > 1 && isAdmin && <button type="button" className="linkish small" disabled={v.winner.isPending} onClick={() => v.winner.mutate(txt.random)}>{txt.letTheLampDecide}</button>}
      {vote.winnerId && !canStart && <div className="how">{txt.anAdminStartsIt}</div>}
      {runner && <div className="d runner">{txt.runnerUp(title(runner.clubBookId), runner.points, runner.points === 1 ? txt.point : txt.points)}</div>}
      {err && <p className="error small">{describeError(err)}</p>}
    </div>
  );
}

/** The circle to the left of a row: your rank, or an empty ring inviting one. Hidden once the vote closes. */
export function RankMark({ rank, onClick, disabled, approval }: { rank: number | null; onClick: () => void; disabled?: boolean; approval?: boolean }) {
  const label = approval ? (rank ? txt.tickedTapToUntick : txt.tickThisBook) : rank ? txt.rankedTapToRemove(rank) : txt.rankThisBook;
  return (
    <button type="button" className={`rank${rank ? " on" : ""}`} aria-pressed={rank !== null} aria-label={label} disabled={disabled} onClick={onClick}>
      {rank ? (approval ? "✓" : rank) : "·"}
    </button>
  );
}

/** Points, a small bar and the faces of who ranked it, under a row. */
export function Tally({ vote, id, max }: { vote: VoteView; id: string; max: number }) {
  const e = vote.entries.find((x) => x.clubBookId === id);
  if (!e) return null;
  return (
    <div className="tally">
      <span className="pts">{vote.method === "approval" ? `${e.points} ${e.points === 1 ? txt.tick : txt.ticks}` : txt.pts(e.points)}</span>
      <span className="bar"><i style={{ width: `${max > 0 ? (e.points / max) * 100 : 0}%` }} /></span>
      <span className="faces">{e.voters.map((vv) => <span key={vv.user.id} className="face"><Avatar name={vv.user.name} image={vv.user.image} />{vote.method !== "approval" && <u>{vv.rank}</u>}</span>)}</span>
    </div>
  );
}
